import React, {FC} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {FieldDescription} from '../../../../../libs/shared/ui/src/components/fields/fieldDescription';
import {StyledRequiredSpan} from '../../../../../libs/shared/ui/src/components/fields/fieldLabel';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {DelayParameter} from '../../../../settings/editors/commons/paramModel';
import {InputDelayHHMM} from '../../../../settings/rules/ruleEditor/inputs/InputDelayHHMM';
import {
  StyledFormDescriptionDiv,
  StyledOnboardingFormTitleDiv,
  StyledOnboardingFormTitlesDiv,
} from '../onboardingWelcomeHelpers';
import {OnboardingWelcomeSlide, OnboardingWelcomeSlideProps} from '../onboardingWelcomeSlide';

/*
 * Props.
 */

export interface OnboardingWorkflowsSlaRuleSlideProps
  extends Pick<OnboardingWelcomeSlideProps, 'navPrimary' | 'navSecondary'> {
  slaReplyTimeGoal: DelayParameter | undefined;
  onChangeSlaReplyTimeGoal: (parameter: DelayParameter) => void;
}

/*
 * Styles.
 */

const StyledHalfContainer = styled.div`
  flex: 0 0 50%;
`;

const StyledInputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  > * {
    width: fit-content;
  }
`;

const StyledContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const StyledCtaWrapper = styled.div`
  font-weight: ${fontWeights.semibold};
  font-size: ${fontSizes.small};
  color: ${(p) => p.theme.greys.shade70};
`;

const StyledFieldDescriptionSpan = styled.span`
  line-height: 18px;
`;

/*
 * Component.
 */

export const OnboardingWorkflowsSlaRuleSlide: FC<OnboardingWorkflowsSlaRuleSlideProps> = ({
  slaReplyTimeGoal,
  onChangeSlaReplyTimeGoal,
  ...slideProps
}) => (
  <OnboardingWelcomeSlide {...slideProps} isLeftContent>
    <StyledHalfContainer>
      <StyledOnboardingFormTitlesDiv>
        <StyledOnboardingFormTitleDiv>
          <Trans id="onboardingwelcome-workflows-sla-rule-title">
            Create a rule to ensure timely response
          </Trans>
        </StyledOnboardingFormTitleDiv>
      </StyledOnboardingFormTitlesDiv>
      <StyledFormDescriptionDiv>
        <Trans id="onboardingwelcome-workflows-sla-rule-description">
          Set your response time goal for this inbox. This rule will notify your team when a breach happens.
        </Trans>
      </StyledFormDescriptionDiv>
      <StyledContentWrapper>
        <StyledCtaWrapper>
          <Trans id="onboardingwelcome-workflows-sla-rule-cta">
            What&rsquo;s your team&rsquo;s reply time goal for this inbox?
            <StyledRequiredSpan>*</StyledRequiredSpan>
          </Trans>
        </StyledCtaWrapper>
        <StyledInputWrapper>
          <InputDelayHHMM
            granularity="hours"
            maxHH={2399} // Max delay is 100 days or 2400 hours, so input allows 2399 and 59s
            selectedDelay={slaReplyTimeGoal}
            onDelayChange={onChangeSlaReplyTimeGoal}
            shouldAutoFocus
          />
        </StyledInputWrapper>
        <FieldDescription
          message={
            <StyledFieldDescriptionSpan>
              <Trans id="onboardingwelcome-workflows-sla-rule-suggestion">
                As a good starting point, we recommend 24 hours for emails, and 5 minutes for chat. You can
                always change this later.
              </Trans>
            </StyledFieldDescriptionSpan>
          }
        />
      </StyledContentWrapper>
    </StyledHalfContainer>
  </OnboardingWelcomeSlide>
);
